import React, { useEffect, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useOnboardingGuard } from 'hooks/useOnboardingGuard';
import { isSocialLogin, isEmailOtpVerified, getCurrentUserEmail, setEmailOtpVerified, obSet, OB_KEYS } from 'utils/onboardingStorage';
import PageLoadingSkeleton from 'components/ui/PageLoadingSkeleton';
import './OnboardingProtectedRoute.css';

/**
 * Route component that ensures users complete onboarding before accessing main app
 * Redirects incomplete users to their next onboarding step
 */
export const OnboardingProtectedRoute: React.FC = () => {
  const { progress, isLoading, refreshProgress, getPathFromStepId } = useOnboardingGuard({
    enabled: true,
    showNotifications: false // Don't show notifications during route checks
  });
  
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initializeProgress = async () => {
      if (!progress && !isLoading) {
        await refreshProgress();
      }
      setIsInitialized(true);
    };

    initializeProgress();
  }, [progress, isLoading, refreshProgress]);

  // Show loading bar while checking onboarding status — never a blank screen
  if (isLoading || !isInitialized) {
    return <PageLoadingSkeleton />;
  }

  const userEmail = getCurrentUserEmail();

  const isBackendVerified =
    progress?.email_verified === true ||
    (progress as any)?.email_verified === 1 ||
    (progress as any)?.email_verified === '1';

  if (isBackendVerified && userEmail) {
    setEmailOtpVerified(userEmail, true);
    obSet(OB_KEYS.EMAIL_VERIFIED);
  }

  const isVerified = isEmailOtpVerified(userEmail) || isBackendVerified || isSocialLogin();

  // If email is not verified, redirect to email-otp (social logins are exempt)
  if (!isVerified) {
    if (userEmail) {
      localStorage.setItem('tnb_otp_email', userEmail);
    }
    return <Navigate to="/onboarding/email-otp" replace />;
  }

  // If progress data couldn't be fetched (API error / network issue),
  // allow access rather than incorrectly bouncing a logged-in user to onboarding.
  if (!progress) {
    console.warn('No onboarding progress found — allowing access (API may be unavailable)');
    return <Outlet />;
  }

  // If onboarding is complete, allow access to main app
  if (progress.is_complete) {
    return <Outlet />;
  }

  // Find the next incomplete step and redirect there
  const nextIncompleteStep = progress.steps.find(step => step.status_id === 2);
  
  if (nextIncompleteStep) {
    const targetRoute = getPathFromStepId(nextIncompleteStep.step_id);
    return <Navigate to={targetRoute} replace />;
  }

  // Fallback - redirect to basic details if we can't determine next step
  console.warn('Could not determine next onboarding step, redirecting to basic details');
  return <Navigate to="/onboarding/basic-details" replace />;
};

export default OnboardingProtectedRoute;